import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { AuthShell, GoogleButton } from "./AuthShell";
import { formatApiError } from "@/lib/api";
import { enableRunnerPush, saveRunnerSports, pushSupported, RUNNER_CATEGORIES } from "@/lib/push";
import { isStandalone, getPlatform } from "@/lib/device";
import { Bell } from "lucide-react";
import { toast } from "sonner";

export default function Register() {
  const { register } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", password: "", confirm_password: "" });
  const [notify, setNotify] = useState(false);
  const [sports, setSports] = useState(RUNNER_CATEGORIES.map((c) => c.slug));
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const toggleSport = (slug) => setSports((s) => s.includes(slug) ? s.filter((x) => x !== slug) : [...s, slug]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (form.password !== form.confirm_password) { setError("Passwords do not match"); return; }
    setBusy(true);
    try {
      await register({ name: form.name, email: form.email, password: form.password });
      toast.success("Account created!");
      if (notify && sports.length) {
        try { await saveRunnerSports(sports); } catch { /* ignore */ }
        const canEnable = pushSupported() && (isStandalone() || getPlatform() !== "ios");
        if (canEnable) {
          try { await enableRunnerPush(sports); toast.success("Race alerts enabled!"); }
          catch (e3) { toast.message(e3.message || "Finish enabling alerts in your profile."); }
        } else {
          toast.message("Saved! Add YourRaceFinder to your Home Screen, then tap Enable Notifications in your profile to finish.");
        }
      }
      nav("/dashboard", { replace: true });
    } catch (e2) {
      setError(formatApiError(e2.response?.data?.detail) || e2.message);
    } finally { setBusy(false); }
  };

  return (
    <AuthShell title="Sign Up" subtitle="Choose how you want to use YourRaceFinder">
      <div className="grid grid-cols-2 gap-3 mb-6">
        <div className="border-2 border-[#0a1420] rounded-xl p-3 text-center bg-[#0a1420] text-white" data-testid="signup-role-runner">
          <div className="font-display font-bold uppercase text-sm">I'm a Runner</div>
          <div className="text-[11px] opacity-80">Save &amp; plan races</div>
        </div>
        <button type="button" onClick={() => nav("/organizer/register")} className="border-2 border-slate-300 rounded-xl p-3 text-center hover:border-[#22d3ee] transition-colors" data-testid="signup-role-organizer">
          <div className="font-display font-bold uppercase text-sm text-slate-700">I'm an Organizer</div>
          <div className="text-[11px] text-slate-500">List your race</div>
        </button>
      </div>
      <form onSubmit={submit} className="space-y-4">
        {error && <div className="bg-red-50 text-red-600 text-sm p-3 rounded-lg" data-testid="register-error">{error}</div>}
        <div>
          <label className="text-sm font-semibold text-slate-700">Full Name</label>
          <input required value={form.name} onChange={set("name")} data-testid="register-name"
            className="mt-1 w-full border border-slate-300 rounded-lg px-4 py-3 focus:border-[#22d3ee] outline-none" />
        </div>
        <div>
          <label className="text-sm font-semibold text-slate-700">Email</label>
          <input type="email" required value={form.email} onChange={set("email")} data-testid="register-email"
            className="mt-1 w-full border border-slate-300 rounded-lg px-4 py-3 focus:border-[#22d3ee] outline-none" />
        </div>
        <div>
          <label className="text-sm font-semibold text-slate-700">Password</label>
          <input type="password" required minLength={6} value={form.password} onChange={set("password")} data-testid="register-password"
            className="mt-1 w-full border border-slate-300 rounded-lg px-4 py-3 focus:border-[#22d3ee] outline-none" />
        </div>
        <div>
          <label className="text-sm font-semibold text-slate-700">Confirm Password</label>
          <input type="password" required minLength={6} value={form.confirm_password} onChange={set("confirm_password")} data-testid="register-confirm-password"
            className="mt-1 w-full border border-slate-300 rounded-lg px-4 py-3 focus:border-[#22d3ee] outline-none" />
        </div>
        <div className="border border-slate-200 rounded-xl p-4 bg-slate-50/60">
          <label className="flex items-start gap-2.5 cursor-pointer">
            <input type="checkbox" checked={notify} onChange={(e) => setNotify(e.target.checked)} data-testid="register-notify-optin" className="mt-1 w-4 h-4" />
            <span>
              <span className="font-semibold text-slate-800 text-sm flex items-center gap-1.5"><Bell size={14} className="text-[#06b6d4]" /> Notify me about new races</span>
              <span className="block text-xs text-slate-500">Free alerts when a new race in your chosen sports is published. For the best experience on iPhone &amp; Android, add YourRaceFinder to your Home Screen — we'll guide you in your profile. Permission is requested only when you tap Enable.</span>
            </span>
          </label>
          {notify && (
            <div className="flex flex-wrap gap-2 mt-3" data-testid="register-notify-sports">
              {RUNNER_CATEGORIES.map((c) => (
                <button type="button" key={c.slug} onClick={() => toggleSport(c.slug)} data-testid={`register-sport-${c.slug}`}
                  className={`px-3 py-1.5 rounded-lg text-xs font-bold border transition-colors ${sports.includes(c.slug) ? "bg-[#0a1420] text-white border-[#0a1420]" : "border-slate-300 text-slate-600"}`}>{c.name}</button>
              ))}
            </div>
          )}
        </div>
        <button disabled={busy} type="submit" data-testid="register-submit"
          className="w-full bg-[#0a1420] text-white font-bold py-3 rounded-lg hover:bg-[#22d3ee] hover:text-[#0a1420] transition-colors disabled:opacity-60">
          {busy ? "Creating…" : "Create Account"}
        </button>
      </form>
      <div className="flex items-center gap-3 my-6"><div className="flex-1 h-px bg-slate-200" /><span className="text-xs text-slate-400">OR</span><div className="flex-1 h-px bg-slate-200" /></div>
      <GoogleButton label="Sign up with Google" />
      <p className="text-center text-sm text-slate-500 mt-6">Already have an account? <Link to="/login" className="text-[#06b6d4] font-semibold hover:underline">Log In</Link></p>
      <p className="text-center text-sm text-slate-500 mt-2">Race organizer? <Link to="/organizer/register" className="text-[#06b6d4] font-semibold hover:underline">List Your Race</Link></p>
    </AuthShell>
  );
}
